/**
 * ProvenancePanel — trust badge + register/attest actions.
 *
 * Shows three trust signals, hiding all crypto plumbing from the user:
 *   - Chain integrity — every ledger entry re-derived and hash-linked.
 *   - "Sealed by Absolute Pinks" — AP's KMS seal is valid.
 *   - "Attested by <X>" — an expert's WebAuthn assertion is bound (or none).
 *
 * Overall trust is allOk: ALL checks pass, not just seal. A tampered entry
 * or head mismatch surfaces as "Integrity failed" even if the seal passes.
 *
 * Attestation is online-only (constitutional — the panel shows an explicit
 * offline state, never silently degrades). Reads and verification are
 * available offline against the local ProofBundle.
 *
 * Capacitor readiness:
 *   - No firebase/* imports (trust layer is API-backed, not Firestore).
 *   - All WebAuthn calls route through src/lib/trust/webauthn.ts.
 *   - No navigator.onLine — uses useOnlineStatus() hook.
 *   - No localStorage for sensitive data.
 *   - No custom URL schemes.
 */

import { Button } from '@/components/Button';
import { Pill } from '@/components/Pill';
import { F } from '@/design/fonts';
import { RADIUS, SPACE, T, TRANSITION } from '@/design/tokens';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { env } from '@/lib/env';
import { downloadOfflineVerifier, downloadProofBundleJson } from '@/lib/trust/bundle';
import { spkiPemFingerprint } from '@/lib/trust/crypto';
import type { VerifyResult } from '@/lib/trust/verify';
import { verifyProofBundle } from '@/lib/trust/verify';
import { isWebAuthnAvailable } from '@/lib/trust/webauthn';
import type { Attestation, ProofBundle } from '@/types/domain';
import {
  ChevronDown,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  ShieldQuestion,
  ShieldX,
  WifiOff,
} from 'lucide-react';
import type { CSSProperties } from 'react';
import { useEffect, useReducer, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns a short disambiguation fingerprint from a base64url credId.
 * Format: "attester ·· <first6>…<last4>" (e.g. "attester ·· a1b2c3…9f0c").
 *
 * Never renders the full credential ID — it's a WebAuthn blob, not a human
 * identifier. The first 6 + last 4 chars are enough for disambiguation
 * without leaking the full credential structure.
 *
 * TODO(contract): proof bundle should carry the attester's display label
 * (AttesterIdentity.label) — rust-backend
 */
function credIdFingerprint(credId: string): string {
  if (credId.length <= 10) return `attester ·· ${credId}`;
  const head = credId.slice(0, 6);
  const tail = credId.slice(-4);
  return `attester ·· ${head}…${tail}`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ProvenancePanelProps {
  vehicleId: string;
  /** The head hash currently shown in the UI (from dossier). Used to detect head-mismatch. */
  expectedHead?: string;
  bundle: ProofBundle | null;
  loading: boolean;
  error: Error | null;
  onRegister: () => void;
  onAttest: () => void;
  /**
   * Whether the panel body starts expanded. Defaults to false (collapsed).
   * Set to true externally when overall status is 'failed' (tampered record
   * must not be hidden). Controlled by the parent via provenanceOverallStatus.
   */
  defaultOpen?: boolean;
  /**
   * Stable HTML id for the panel body — used as aria-controls target by the
   * TrustChip button in VehicleDetail's title row.
   */
  bodyId?: string;
  /**
   * Called whenever the disclosure open/close state changes. The parent uses
   * this to keep the TrustChip's aria-expanded in sync.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Identity dispute state derived from the vehicle doc's server-controlled
   * `disputeStatus` field. The parent maps the wire value:
   *   'underReview' → 'under-review'; 'contested' → 'contested'; else 'none'.
   * gg-web never writes this — it is always server-set.
   * When present and !== 'none', a notice block is rendered at the top of the
   * panel body explaining the dispute axis (NOT a crypto failure).
   */
  disputeState?: 'none' | 'under-review' | 'contested';
  /**
   * Fired after client-side verification completes (or errors). The parent uses
   * these values to derive provenanceOverallStatus for the TrustChip without
   * duplicating the async verify logic.
   *
   * `verifiedHead` is the headHash of the bundle that was verified (or null
   * when reporting a mid-flight / error state). The parent uses this to detect
   * when a new bundle has arrived but the verdict for the old bundle is still
   * in state — in that window it should show 'checking', not stale green/red.
   */
  onVerifyResult?: (result: {
    integrityStatus: 'ok' | 'fail' | 'none';
    sealStatus: 'ok' | 'fail' | 'none';
    attestationVerifyStatus: 'ok' | 'fail' | 'none';
    verifyError: string | null;
    verifying: boolean;
    /** headHash of the bundle this verdict applies to. Null during in-flight. */
    verifiedHead: string | null;
  }) => void;
}

// ---------------------------------------------------------------------------
// Deployment-context WebAuthn config
//
// Env-configured origins and rpIds so verification (and the exported offline
// verifier) match the deployment context, not the dev defaults baked into
// verify.ts. VITE_TRUST_ALLOWED_ORIGINS is a comma-separated list.
// ---------------------------------------------------------------------------

const TRUST_ALLOWED_ORIGINS = env.VITE_TRUST_ALLOWED_ORIGINS.split(',').map((s) => s.trim());
// 'localhost' is appended in dev only — these lists are baked into exported
// offline verifiers, which must not accept dev rpIds in production artifacts.
const TRUST_ALLOWED_RP_IDS = import.meta.env.DEV
  ? [env.VITE_TRUST_RP_ID, 'localhost']
  : [env.VITE_TRUST_RP_ID];

// ---------------------------------------------------------------------------
// Local state
// ---------------------------------------------------------------------------

interface PanelState {
  verifyResult: VerifyResult | null;
  verifying: boolean;
  verifyError: string | null;
  webAuthnAvailable: boolean | null;
  /** Colon-hex SHA-256 of the seal public key's SPKI DER, for out-of-band pinning. */
  sealFingerprint: string | null;
}

type PanelAction =
  | { type: 'verify_start' }
  | { type: 'verify_done'; result: VerifyResult }
  | { type: 'verify_error'; message: string }
  | { type: 'webauthn_checked'; available: boolean }
  | { type: 'fingerprint_done'; fingerprint: string | null };

function panelReducer(state: PanelState, action: PanelAction): PanelState {
  switch (action.type) {
    case 'verify_start':
      return { ...state, verifying: true, verifyResult: null, verifyError: null };
    case 'verify_done':
      return { ...state, verifying: false, verifyResult: action.result, verifyError: null };
    case 'verify_error':
      return { ...state, verifying: false, verifyResult: null, verifyError: action.message };
    case 'webauthn_checked':
      return { ...state, webAuthnAvailable: action.available };
    case 'fingerprint_done':
      return { ...state, sealFingerprint: action.fingerprint };
  }
}

// ---------------------------------------------------------------------------
// StatusPill — renders a canonical <Pill> for the three trust signal states.
// Declared at module level (not inside ProvenancePanel) to satisfy the
// react-hooks/no-nested-components rule.
//
// Uses green/red tinted pills for ok/fail, neutral variant for pending/none.
// This replaces the old badgeStyle function which used 10%/25% color-mix —
// Pill uses the canonical 15%/35% weight defined in DESIGN.md.
// ---------------------------------------------------------------------------

function StatusPill({ status, children }: { status: 'ok' | 'fail' | 'none'; children: string }) {
  if (status === 'ok')
    return (
      <Pill color="green" size="sm">
        {children}
      </Pill>
    );
  if (status === 'fail')
    return (
      <Pill color="red" size="sm">
        {children}
      </Pill>
    );
  return (
    <Pill variant="neutral" size="sm">
      {children}
    </Pill>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProvenancePanel({
  vehicleId,
  expectedHead,
  bundle,
  loading,
  error,
  onRegister,
  onAttest,
  defaultOpen = false,
  bodyId,
  onOpenChange,
  onVerifyResult,
  disputeState = 'none',
}: ProvenancePanelProps) {
  const isOnline = useOnlineStatus();

  const [state, dispatch] = useReducer(panelReducer, {
    verifyResult: null,
    verifying: false,
    verifyError: null,
    webAuthnAvailable: null,
    sealFingerprint: null,
  });

  // Notify-callback held in a ref so it is NOT a dependency of the verify
  // effect. If it were (and the parent passed an unstable identity), every
  // verify result would re-trigger the effect → re-verify → infinite loop.
  // The ref keeps the latest callback without re-running verification.
  const onVerifyResultRef = useRef(onVerifyResult);
  useEffect(() => {
    onVerifyResultRef.current = onVerifyResult;
  });

  // Disclosure open/close state. Starts at defaultOpen; synced when the
  // parent flips defaultOpen (e.g. when a failed result arrives and the
  // parent force-opens). Subsequent user toggles are fully local.
  const [isOpen, setIsOpen] = useState(defaultOpen);
  useEffect(() => {
    // Only force-open when the parent signals it; never force-close.
    if (defaultOpen && !isOpen) {
      setIsOpen(true);
      onOpenChange?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOpen]);

  function toggleOpen() {
    const next = !isOpen;
    setIsOpen(next);
    onOpenChange?.(next);
  }

  useEffect(() => {
    if (!bundle) return;
    let cancelled = false;
    dispatch({ type: 'verify_start' });
    onVerifyResultRef.current?.({
      integrityStatus: 'none',
      sealStatus: 'none',
      attestationVerifyStatus: 'none',
      verifyError: null,
      verifying: true,
      verifiedHead: null, // not yet verified
    });
    verifyProofBundle(bundle, {
      ...(expectedHead !== undefined ? { expectedHead } : {}),
      expectedVehicleId: vehicleId,
      allowedOrigins: TRUST_ALLOWED_ORIGINS,
      allowedRpIds: TRUST_ALLOWED_RP_IDS,
    })
      .then((result) => {
        if (!cancelled) {
          dispatch({ type: 'verify_done', result });
          // Derive the same signal values the panel computes from result,
          // so the parent can call provenanceOverallStatus with the same inputs.
          const iStatus: 'ok' | 'fail' | 'none' =
            result.integrity.status === 'ok' &&
            result.headMatches.status === 'ok' &&
            result.vehicleIdMatches.status === 'ok'
              ? 'ok'
              : 'fail';
          const sStatus: 'ok' | 'fail' | 'none' = result.seal.status === 'ok' ? 'ok' : 'fail';
          const aStatus: 'ok' | 'fail' | 'none' =
            result.attestation !== undefined && result.attestation !== null
              ? result.attestation.status === 'ok'
                ? 'ok'
                : 'fail'
              : 'none';
          onVerifyResultRef.current?.({
            integrityStatus: iStatus,
            sealStatus: sStatus,
            attestationVerifyStatus: aStatus,
            verifyError: null,
            verifying: false,
            verifiedHead: bundle.headHash, // verdict applies to this specific head
          });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : String(err);
          dispatch({ type: 'verify_error', message });
          onVerifyResultRef.current?.({
            integrityStatus: 'none',
            sealStatus: 'none',
            attestationVerifyStatus: 'none',
            verifyError: message,
            verifying: false,
            verifiedHead: null, // error — no verified head
          });
        }
      });
    return () => {
      cancelled = true;
    };
    // Key on the bundle's HEAD HASH (its cryptographic identity), not the
    // bundle object reference. The query layer (and test mocks) can hand back a
    // fresh object on every render with identical content; depending on the
    // object would re-run verify each render → setState → re-render → infinite
    // loop. The head hash changes iff the ledger head actually changed, which is
    // exactly when re-verification is warranted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundle?.headHash, expectedHead, vehicleId]);

  useEffect(() => {
    void isWebAuthnAvailable().then((available) => {
      dispatch({ type: 'webauthn_checked', available });
    });
  }, []);

  // Seal-key fingerprint — surfaced so "confirm the key out-of-band" is
  // actionable, not just an instruction.
  useEffect(() => {
    if (!bundle) {
      dispatch({ type: 'fingerprint_done', fingerprint: null });
      return;
    }
    let cancelled = false;
    spkiPemFingerprint(bundle.sealPublicKey)
      .then((fp) => {
        if (!cancelled) dispatch({ type: 'fingerprint_done', fingerprint: fp });
      })
      .catch(() => {
        if (!cancelled) dispatch({ type: 'fingerprint_done', fingerprint: null });
      });
    return () => {
      cancelled = true;
    };
  }, [bundle]);

  // ---------------------------------------------------------------------------
  // Styles
  // ---------------------------------------------------------------------------

  const containerStyle: CSSProperties = {
    border: `1px solid ${T.border}`,
    borderLeft: `3px solid ${T.accent}`,
    borderRadius: RADIUS.md,
    background: T.card,
    overflow: 'hidden',
    fontFamily: F.body,
  };

  const headerStyle: CSSProperties = {
    padding: `${SPACE.sm}px ${SPACE.md}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACE.sm,
  };

  const bodyStyle: CSSProperties = {
    padding: `${SPACE.md}px ${SPACE.md}px`,
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.md,
  };

  const sectionStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: SPACE.md,
  };

  // ---------------------------------------------------------------------------
  // Derived trust signals (full result, not just seal)
  // ---------------------------------------------------------------------------

  const r = state.verifyResult;

  // Overall integrity signal: chain + head consistency + vehicleId consistency.
  const integrityStatus: 'ok' | 'fail' | 'none' = r
    ? r.integrity.status === 'ok' &&
      r.headMatches.status === 'ok' &&
      r.vehicleIdMatches.status === 'ok'
      ? 'ok'
      : 'fail'
    : 'none';

  const integrityFailReason: string | undefined = r
    ? (r.integrity.failReason ??
      r.headMatches.failReason ??
      r.vehicleIdMatches.failReason ??
      r.integrity.entries.find((e) => e.status === 'fail')?.failReason)
    : undefined;

  const sealStatus: 'ok' | 'fail' | 'none' = r ? (r.seal.status === 'ok' ? 'ok' : 'fail') : 'none';

  const attestationVerifyStatus: 'ok' | 'fail' | 'none' =
    r?.attestation !== undefined && r.attestation !== null
      ? r.attestation.status === 'ok'
        ? 'ok'
        : 'fail'
      : 'none';

  // The attestation from the bundle for display.
  const attestation: Attestation | null = bundle?.attestation ?? null;

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div style={containerStyle} aria-label="Provenance panel loading">
        <div style={{ ...headerStyle, borderBottom: `1px solid ${T.border}` }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Provenance</span>
        </div>
        <div style={{ padding: `${SPACE.md}px ${SPACE.md}px`, color: T.textMuted, fontSize: 13 }}>
          Loading provenance data…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...containerStyle, borderLeftColor: T.red }} role="alert">
        <div style={{ ...headerStyle, borderBottom: `1px solid ${T.border}` }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Provenance</span>
          <StatusPill status="fail">Error</StatusPill>
        </div>
        <div style={{ padding: `${SPACE.md}px ${SPACE.md}px`, color: T.red, fontSize: 13 }}>
          {error.message}
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  const vehicleLabel = `Vehicle ${vehicleId}`;
  const panelBodyId = bodyId ?? `provenance-body-${vehicleId}`;

  return (
    <section aria-label="Provenance" style={containerStyle}>
      {/* Header — acts as the disclosure toggle.
          h2 wrapper follows ARIA APG accordion pattern so "Provenance" appears
          in the document heading outline (matching Collapsible). */}
      <h2 style={{ margin: 0 }}>
        <button
          type="button"
          aria-expanded={isOpen}
          aria-controls={panelBodyId}
          onClick={toggleOpen}
          style={{
            ...headerStyle,
            width: '100%',
            background: 'transparent',
            cursor: 'pointer',
            border: 'none',
            borderBottom: `1px solid ${T.border}`,
            textAlign: 'left',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Provenance</span>
            {state.verifying && (
              <span
                style={{ fontSize: 11, color: T.textMuted, fontFamily: F.mono }}
                role="status"
                aria-live="polite"
              >
                Verifying…
              </span>
            )}
            {state.verifyError && (
              <span
                style={{ fontSize: 11, color: T.red, fontFamily: F.mono }}
                role="alert"
                aria-live="assertive"
              >
                Verify error
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm }}>
            {!isOnline && (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACE.xs,
                  fontSize: 12,
                  color: T.textMuted,
                }}
                aria-label="You are offline. Attestation actions require a connection."
              >
                <WifiOff size={14} aria-hidden="true" />
                Offline
              </span>
            )}
            <ChevronDown
              size={14}
              aria-hidden="true"
              style={{
                color: T.textMuted,
                transition: `transform ${TRANSITION.fast}`,
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                flexShrink: 0,
              }}
            />
          </div>
        </button>
      </h2>

      {/* Body — collapsible */}
      <div id={panelBodyId} hidden={!isOpen} style={isOpen ? bodyStyle : undefined}>
        {/* --- Dispute notice — rendered above integrity/seal/attestation rows.
              Only shown when server has filed or upheld a dispute.
              Crypto rows are NOT flipped to red because of a dispute — if
              the ledger passes crypto it is intact; the dispute axis is the
              real-world identity claim, not record integrity. --- */}
        {disputeState !== 'none' &&
          (disputeState === 'contested' ? (
            <div
              role="alert"
              aria-live="assertive"
              style={{
                border: `1px solid ${T.red}`,
                borderLeft: `3px solid ${T.red}`,
                borderRadius: RADIUS.md,
                background: `color-mix(in srgb, ${T.red} 12%, transparent)`,
                padding: `${SPACE.sm}px ${SPACE.md}px`,
                display: 'flex',
                alignItems: 'flex-start',
                gap: SPACE.sm,
              }}
            >
              <ShieldX
                size={16}
                color={T.red}
                aria-hidden="true"
                style={{ flexShrink: 0, marginTop: 2 }}
              />
              <div>
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: T.red,
                    margin: 0,
                    fontFamily: F.body,
                  }}
                >
                  Identity disputed
                </p>
                <p
                  style={{
                    fontSize: 12,
                    color: T.text,
                    margin: `${SPACE.xs}px 0 0`,
                    lineHeight: 1.5,
                    fontFamily: F.body,
                  }}
                >
                  {/* TODO: this duplicates DisputeNotice.tsx — consolidate in a follow-up. */}
                  Absolute Pinks reviewed a challenge to this vehicle's stated identity and upheld
                  the challenge: the claim of which car this is may not hold up. The sealed history
                  is intact and untampered. What's in question is the real-world identity, not the
                  record.
                </p>
              </div>
            </div>
          ) : (
            <div
              role="status"
              aria-live="polite"
              style={{
                border: `1px solid ${T.border}`,
                borderRadius: RADIUS.md,
                background: `color-mix(in srgb, ${T.border} 30%, transparent)`,
                padding: `${SPACE.sm}px ${SPACE.md}px`,
                display: 'flex',
                alignItems: 'flex-start',
                gap: SPACE.sm,
              }}
            >
              <ShieldAlert
                size={14}
                color={T.textMuted}
                aria-hidden="true"
                style={{ flexShrink: 0, marginTop: 2 }}
              />
              <div>
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: T.text,
                    margin: 0,
                    fontFamily: F.body,
                  }}
                >
                  Identity under review
                </p>
                <p
                  style={{
                    fontSize: 12,
                    color: T.textMuted,
                    margin: `${SPACE.xs}px 0 0`,
                    lineHeight: 1.5,
                    fontFamily: F.body,
                  }}
                >
                  Someone has questioned whether this is the vehicle it claims to be. Absolute Pinks
                  is reviewing the challenge. The sealed history below is unaffected.
                </p>
              </div>
            </div>
          ))}
        {/* --- Integrity status (chain + head consistency) --- */}
        <div style={sectionStyle}>
          <div style={{ flexShrink: 0, marginTop: 2 }}>
            {integrityStatus === 'ok' ? (
              <ShieldCheck size={18} color={T.green} aria-hidden="true" />
            ) : integrityStatus === 'fail' ? (
              <ShieldOff size={18} color={T.red} aria-hidden="true" />
            ) : (
              <ShieldQuestion size={18} color={T.textMuted} aria-hidden="true" />
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: SPACE.sm,
                flexWrap: 'wrap' as const,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Ledger integrity</span>
              {bundle ? (
                <StatusPill status={integrityStatus}>
                  {integrityStatus === 'ok'
                    ? 'Verified'
                    : integrityStatus === 'fail'
                      ? 'Failed'
                      : 'Verifying…'}
                </StatusPill>
              ) : null}
            </div>
            <p style={{ fontSize: 12, color: T.textMuted, marginTop: SPACE.xs, lineHeight: 1.5 }}>
              {bundle
                ? integrityStatus === 'ok'
                  ? 'All ledger entries hash-verified; head matches.'
                  : integrityStatus === 'fail'
                    ? `Integrity failed: ${integrityFailReason ?? 'unknown error'}`
                    : 'Verifying ledger…'
                : 'No provenance record exists for this vehicle yet.'}
            </p>
          </div>
        </div>

        {/* Divider */}
        <hr style={{ border: 'none', borderTop: `1px solid ${T.border}`, margin: 0 }} />

        {/* --- Seal status --- */}
        <div style={sectionStyle}>
          <div style={{ flexShrink: 0, marginTop: 2 }}>
            {sealStatus === 'ok' ? (
              <ShieldCheck size={18} color={T.green} aria-hidden="true" />
            ) : sealStatus === 'fail' ? (
              <ShieldOff size={18} color={T.red} aria-hidden="true" />
            ) : (
              <ShieldQuestion size={18} color={T.textMuted} aria-hidden="true" />
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: SPACE.sm,
                flexWrap: 'wrap' as const,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                Sealed by Absolute Pinks
              </span>
              {bundle ? (
                <StatusPill status={sealStatus}>
                  {sealStatus === 'ok'
                    ? 'Verified'
                    : sealStatus === 'fail'
                      ? 'Invalid'
                      : 'Verifying…'}
                </StatusPill>
              ) : null}
            </div>
            <p style={{ fontSize: 12, color: T.textMuted, marginTop: SPACE.xs, lineHeight: 1.5 }}>
              {bundle
                ? sealStatus === 'ok'
                  ? "Absolute Pinks' signing-key seal is valid."
                  : sealStatus === 'fail'
                    ? `Seal verification failed: ${r?.seal.failReason ?? 'unknown error'}`
                    : 'Verifying seal…'
                : 'No provenance record exists for this vehicle yet.'}
            </p>
          </div>
        </div>

        {/* Divider */}
        <hr style={{ border: 'none', borderTop: `1px solid ${T.border}`, margin: 0 }} />

        {/* --- Attestation status --- */}
        <div style={sectionStyle}>
          <div style={{ flexShrink: 0, marginTop: 2 }}>
            {attestationVerifyStatus === 'ok' ? (
              <ShieldCheck size={18} color={T.green} aria-hidden="true" />
            ) : attestationVerifyStatus === 'fail' ? (
              <ShieldOff size={18} color={T.red} aria-hidden="true" />
            ) : (
              <ShieldQuestion size={18} color={T.textMuted} aria-hidden="true" />
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: SPACE.sm,
                flexWrap: 'wrap' as const,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                {attestationVerifyStatus === 'ok' && attestation
                  ? 'Attested by a registered attester'
                  : 'Attester attestation'}
              </span>
              {bundle ? (
                <StatusPill status={attestationVerifyStatus}>
                  {attestationVerifyStatus === 'ok'
                    ? 'Verified'
                    : attestationVerifyStatus === 'fail'
                      ? 'Invalid'
                      : 'None'}
                </StatusPill>
              ) : null}
            </div>
            <p style={{ fontSize: 12, color: T.textMuted, marginTop: SPACE.xs, lineHeight: 1.5 }}>
              {attestationVerifyStatus === 'ok' && attestation
                ? attestation.statement
                : attestationVerifyStatus === 'fail'
                  ? `Attestation verification failed: ${r?.attestation?.failReason ?? 'unknown'}`
                  : 'No attestation has been recorded for this vehicle yet.'}
            </p>
            {attestationVerifyStatus === 'ok' && attestation && (
              <p style={{ fontSize: 11, color: T.textMuted, marginTop: SPACE.xs }}>
                <span style={{ fontFamily: F.mono }}>
                  {credIdFingerprint(attestation.attesterCredId)}
                </span>
                {' · '}
                {attestation.createdAt.slice(0, 10)}
                {r?.attestation?.attestedSeq !== undefined &&
                bundle &&
                r.attestation.attestedSeq < bundle.entries.length - 1
                  ? ` · attested at entry ${r.attestation.attestedSeq + 1} of ${bundle.entries.length} (earlier ledger state)`
                  : ''}
              </p>
            )}
          </div>
        </div>

        {/* --- Online-only actions --- */}
        {!isOnline ? (
          <div
            role="status"
            aria-live="polite"
            style={{
              background: `color-mix(in srgb, ${T.border} 30%, transparent)`,
              border: `1px solid ${T.border}`,
              borderRadius: RADIUS.md,
              padding: `${SPACE.sm}px ${SPACE.sm}px`,
              fontSize: 12,
              color: T.textMuted,
              display: 'flex',
              alignItems: 'center',
              gap: SPACE.sm,
            }}
          >
            <WifiOff size={14} aria-hidden="true" />
            Attestation actions require an internet connection.
          </div>
        ) : (
          <div style={{ display: 'flex', gap: SPACE.sm, flexWrap: 'wrap' as const }}>
            {/* Standard secondary (T.border) — no pink. Attest is the weighted
                action here; give it ink-border weight per DESIGN.md precedent. */}
            <Button
              size="sm"
              variant="secondary"
              disabled={state.webAuthnAvailable === false}
              title={
                state.webAuthnAvailable === false
                  ? 'This device does not support passkey authentication'
                  : undefined
              }
              onClick={onRegister}
            >
              Register as attester
            </Button>

            <Button
              size="sm"
              variant="secondary"
              style={{ borderColor: T.text }}
              disabled={!bundle || state.webAuthnAvailable === false}
              title={
                !bundle
                  ? 'No sealed record to attest'
                  : state.webAuthnAvailable === false
                    ? 'This device does not support passkey authentication'
                    : undefined
              }
              onClick={onAttest}
            >
              Attest
            </Button>
          </div>
        )}

        {/* --- Export / download --- */}
        {bundle && (
          <div
            style={{
              display: 'flex',
              gap: SPACE.sm,
              flexWrap: 'wrap' as const,
              paddingTop: SPACE.xs,
            }}
          >
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                downloadProofBundleJson(bundle);
              }}
              aria-label={`Download proof bundle JSON for ${vehicleLabel}`}
            >
              Download proof bundle
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                downloadOfflineVerifier(bundle, {
                  allowedOrigins: TRUST_ALLOWED_ORIGINS,
                  allowedRpIds: TRUST_ALLOWED_RP_IDS,
                });
              }}
              aria-label={`Download offline verifier HTML for ${vehicleLabel}`}
            >
              Download offline verifier
            </Button>
          </div>
        )}

        {/* --- Honest caveat --- */}
        <p style={{ fontSize: 11, color: T.textMuted, lineHeight: 1.5, marginTop: SPACE.xs }}>
          Verification uses keys pinned by Absolute Pinks. Independent verification requires
          confirming the seal key fingerprint out-of-band.
          {state.sealFingerprint ? (
            <>
              <br />
              Seal key fingerprint (SHA-256):{' '}
              <span style={{ fontFamily: F.mono, wordBreak: 'break-all' }}>
                {state.sealFingerprint}
              </span>
            </>
          ) : null}
        </p>
      </div>
    </section>
  );
}
